import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EmptyIcon } from 'assets/icon/bigicon';
import { ForwardIcon } from 'assets/icon';
import Cards from 'design-components/Cards';
import CategoryDropdown from 'components/CategoryDropdown';
import ModalChildCategoryForm from './ModalChildCategoryForm';
import ModalDelete from 'components/ModalDelete';
import { imgUrl } from 'utils/url';
import {
  fetchChildCategories,
  createChildCategory,
  editChildCategory,
  deleteChildCategory,
  setChildCategory,
} from 'state/actions/childComponentCategory';
import {
  fetchChildComponents,
  deleteChildComponent,
} from 'state/actions/childComponent';
import { fetchMasterConfigs } from 'state/actions/masterConfig';

function ListView({ onMasterConfig, onAddChild, onEditChild }) {
  const [selectedTab, setSelectedTab] = useState('published');
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tempSelectedCategory, setTempSelectedCategory] = useState('all');
  const [modalCategory, setModalCategory] = useState(false);
  const [modalDeleteCategory, setModalDeleteCategory] = useState({
    show: false,
    category: null,
  });
  const [modalDeleteChild, setModalDeleteChild] = useState({
    show: false,
    child: null,
  });
  const [previewMedia, setPreviewMedia] = useState(null);

  const dispatch = useDispatch();
  const { siteId } = useParams();

  const { categoriesList, category, childComponents, masterConfigList } =
    useSelector((state) => ({
      categoriesList: state.childComponentCategory.list,
      category: state.childComponentCategory.category,
      childComponents: state.childComponent.list,
      masterConfigList: state.masterConfig.list,
    }));

  useEffect(() => {
    if (siteId) {
      dispatch(fetchChildCategories({ siteId }));
      dispatch(fetchMasterConfigs({ siteId }));
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    dispatch(
      fetchChildComponents({
        siteId: siteId,
        childComponentCategoryIds: selectedCategory,
        type: selectedTab,
      })
    );
  }, [siteId, selectedTab, selectedCategory, dispatch]);

  const getCategory = (id) => {
    const cat = categoriesList.find((c) => c._id === id);
    return cat ? cat.name : 'All Categories';
  };

  const handleSetPlaylist = () => {
    setSelectedCategory(tempSelectedCategory);
    setPlaylistOpen(false);
  };

  const handleNew = () => {
    dispatch(
      setChildCategory(
        { _id: '', name: '', description: '' },
        () => setModalCategory(true)
      )
    );
  };

  const handleEditCategory = (cat) => {
    dispatch(setChildCategory(cat, () => setModalCategory(true)));
  };

  const handleDeleteCategory = (cat) => {
    setModalDeleteCategory({ show: true, category: cat });
  };

  const handleSetCategory = (updated) => {
    dispatch(setChildCategory(updated));
  };

  const handleCancel = () => {
    setModalCategory(false);
  };

  const onSubmitCategory = (cat) => {
    if (cat._id) {
      dispatch(
        editChildCategory(
          { id: cat._id, name: cat.name, description: cat.description, siteId },
          () => setModalCategory(false)
        )
      );
    } else {
      dispatch(
        createChildCategory(
          { name: cat.name, description: cat.description, siteId },
          () => setModalCategory(false)
        )
      );
    }
  };

  const getTextField = (child) => {
    const dynamicFields = child.dynamicFields || [];
    return dynamicFields
      .filter((f) => f.type === 'TextField')
      .map((f) => f.value)
      .filter(Boolean)
      .join(' · ');
  };

  const getSchedulingDate = (child) => {
    if (!child.startDate || !child.endDate) return { allDay: true };
    return { startDate: child.startDate, endDate: child.endDate };
  };

  const getFirstMedia = (child) => {
    const dynamicFields = child.dynamicFields || [];
    const mediaField = dynamicFields.find((f) => f.type === 'MediaField');
    if (!mediaField) return null;
    return mediaField.value || null;
  };

  const onDeleteChild = () => {
    dispatch(
      deleteChildComponent(modalDeleteChild.child._id, () =>
        setModalDeleteChild({ show: false, child: null })
      )
    );
  };

  const handleDownloadMedia = (media) => {
    if (!media?.location) return;
    const link = document.createElement('a');
    link.href = `${imgUrl}${media.location}`;
    link.download = media.fileName || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onDeleteCategory = () => {
    dispatch(
      deleteChildCategory(
        modalDeleteCategory.category._id,
        siteId,
        () => setModalDeleteCategory({ show: false, category: null })
      )
    );
  };

  const filteredChildren = childComponents;

  return (
    <div className="dynamic-comp">
      <div className="dynamic-comp-header">
        <h2>Dynamic Components</h2>
      </div>

      <div className="dynamic-comp-subheader">
        <div className="media-header-left">
          <div
            className="playlist-wrapper"
            onClick={() => setPlaylistOpen(!playlistOpen)}
          >
            <div className="playlist-selection">
              <ForwardIcon />
              <span>
                {selectedCategory === 'all'
                  ? 'All Categories'
                  : getCategory(selectedCategory)}
              </span>
            </div>
            <i
              className={`feather-chevron-down ${
                playlistOpen ? 'playlist-open' : 'playlist-closed'
              }`}
            />
            <CategoryDropdown
              categories={categoriesList}
              setTempSelectedCategory={(id) => setTempSelectedCategory(id)}
              handleEditCategory={(cat) => handleEditCategory(cat)}
              handleDeleteCategory={(cat) => handleDeleteCategory(cat)}
              modalOpen={playlistOpen}
              handleSetPlaylist={handleSetPlaylist}
              tempSelectedCategory={tempSelectedCategory}
              handleNew={handleNew}
              filter
            />
          </div>
        </div>

        <div className="dynamic-comp-header-actions">
          {onMasterConfig && (
            <button
              className="dynamic-comp-btn-master-config"
              onClick={onMasterConfig}
            >
              <i className="feather-settings" />
              Master Config
            </button>
          )}
          {onAddChild && (
            <button className="dynamic-comp-btn-new-child" onClick={onAddChild}>
              <i className="feather-plus" />
              New Child
            </button>
          )}
        </div>
      </div>

      <div className="media-content-wrapper">
        <div className="media-content">
          <div className="media-tab-wrapper">
            <div className="media-tab-left">
              <div
                onClick={() => setSelectedTab('published')}
                className={`media-tab ${selectedTab === 'published' ? 'active' : ''}`}
              >
                Published Items
              </div>
              <div
                onClick={() => setSelectedTab('unpublished')}
                className={`media-tab ${selectedTab === 'unpublished' ? 'active' : ''}`}
              >
                Unpublished Items
              </div>
              <div
                onClick={() => setSelectedTab('archived')}
                className={`media-tab ${selectedTab === 'archived' ? 'active' : ''}`}
              >
                Archived Items
              </div>
            </div>
          </div>
        </div>
      </div>

      {filteredChildren.length > 0 ? (
        <div className="media-list-chunk">
          {filteredChildren.map((child) => {
            const media = getFirstMedia(child);
            const dateInfo = getSchedulingDate(child);

            const childCategoryName = (child.categories || [])
              .map((c) => c.categoryName)
              .filter(Boolean)
              .join(', ');

            const cardData = {
              name: child.childTitle,
              categoryName: childCategoryName,
              start: dateInfo && !dateInfo.allDay ? dateInfo.startDate : null,
              end: dateInfo && !dateInfo.allDay ? dateInfo.endDate : null,
              type: dateInfo ? (dateInfo.allDay ? 'all' : 'date') : 'all',
              logo: media ? `${imgUrl}${media.location}` : null,
              fileType: media?.fileName?.endsWith('.mp4')
                ? 'video/mp4'
                : media
                ? 'image/jpeg'
                : null,
              modifiedBy: child.modifiedBy || '-',
            };

            return (
              <div key={child._id} className="media-list">
                <Cards
                  data={cardData}
                  type="dynamic"
                  isShowMedia={!!media}
                  editAction={() => onEditChild(child)}
                  deleteAction={() =>
                    setModalDeleteChild({ show: true, child })
                  }
                  previewAction={
                    media ? () => setPreviewMedia(media) : undefined
                  }
                  downloadAction={
                    media ? () => handleDownloadMedia(media) : undefined
                  }
                  isPreview={!!media}
                  isDownload={!!media}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dynamic-comp-empty">
          <img src={EmptyIcon} alt="empty" className="dynamic-comp-empty-icon" />
          <h3>No Data</h3>
          <p>
            You can add a new child component by clicking "New Child". Make
            sure the Master Config is set up before creating any child.
          </p>
        </div>
      )}

      <Modal
        visible={!!previewMedia}
        footer={null}
        onCancel={() => setPreviewMedia(null)}
        className="adsign-modal"
        width={720}
      >
        {previewMedia && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            {previewMedia.fileName?.endsWith('.mp4') ? (
              <video
                controls
                src={`${imgUrl}${previewMedia.location}`}
                style={{ maxWidth: '100%' }}
              />
            ) : (
              <img
                src={`${imgUrl}${previewMedia.location}`}
                alt="Preview"
                style={{ maxWidth: '100%' }}
              />
            )}
          </div>
        )}
      </Modal>

      <ModalDelete
        isVisible={modalDeleteChild.show}
        onCancel={() => setModalDeleteChild({ show: false, child: null })}
        onOk={onDeleteChild}
        title="Delete Child"
        subtitle={`Are you sure you want to delete "${modalDeleteChild.child?.childTitle}"?`}
      />

      <ModalChildCategoryForm
        visible={modalCategory}
        onCancel={handleCancel}
        onCreate={onSubmitCategory}
        category={category}
        setCategory={handleSetCategory}
        isEditing={!!category._id}
      />

      <ModalDelete
        isVisible={modalDeleteCategory.show}
        onCancel={() =>
          setModalDeleteCategory({ show: false, category: null })
        }
        onOk={onDeleteCategory}
        title="Delete Category"
        subtitle={`Are you sure you want to delete "${modalDeleteCategory.category?.name}"?`}
      />
    </div>
  );
}

export default ListView;
